import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const UserContext = createContext(null);

export function useAuth() {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useAuth must be used within a UserContextProvider');
    }
    return context;
}

export function UserContextProvider({ children }) {
    const [user, setUserState] = useState(null);
    const [accessTkn, setAccessTknState] = useState(null);
    const [refreshTkn, setRefreshTknState] = useState(null);
    const [isAuth, setIsAuthState] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        try {
            const savedUser = localStorage.getItem('user');
            const savedAccess = localStorage.getItem('accessTkn');
            const savedRefresh = localStorage.getItem('refreshTkn');

            const userObj = savedUser ? JSON.parse(savedUser) : null;
            const hasValidToken = !!savedAccess;

            setUserState(userObj);
            setAccessTknState(savedAccess);
            setRefreshTknState(savedRefresh);
            setIsAuthState(hasValidToken);
        } catch (e) {
            console.warn('Failed to restore auth from localStorage', e);
            
        }
    }, []);

    //  setters (update state + persist)
    const setUser = useCallback((data) => {
        setUserState(data);
        if (typeof window !== 'undefined') {
            if (data) {
                localStorage.setItem('user', JSON.stringify(data));
            } else {
                localStorage.removeItem('user');
            }
        }
    }, []);


    const setCredentials = useCallback(({ access, refresh }) => {
        const now = new Date().toISOString();
        setUserState(prev => prev || {});
        setAccessTknState(access);
        setRefreshTknState(refresh);
        setIsAuthState(true);

        if (typeof window !== 'undefined') {
            localStorage.setItem('accessTkn', access);
            localStorage.setItem('refreshTkn', refresh);
            localStorage.setItem('refreshTime', now);
        }
    }, []);

    const logout = useCallback(() => {
        setUserState(null);
        setAccessTknState(null);
        setRefreshTknState(null);
        setIsAuthState(false);

        if (typeof window !== 'undefined') {
            localStorage.removeItem('user');
            localStorage.removeItem('accessTkn');
            localStorage.removeItem('refreshTkn');
            localStorage.removeItem('refreshTime');
            sessionStorage.clear(); 
        }
    }, []);


    useEffect(() => {
        if (!accessTkn) return;
        const payload = JSON.parse(atob(accessTkn.split('.')[1]));
        const expMs = payload.exp * 1000;
        const timeout = expMs - Date.now();
        if (timeout > 0) {
            const timer = setTimeout(() => logout(), timeout);
            return () => clearTimeout(timer);
        } else {
            logout();
        }
    }, [accessTkn, logout]);

    return (
        <UserContext.Provider
            value={{
                user,
                isAuth,
                accessTkn,
                refreshTkn,
                setUser,
                setCredentials,
                logout,
            }}
        >
            {children}
        </UserContext.Provider>
    );
}

export default UserContext;